import { Suspense, useEffect, useState } from "react";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../../common/components/ui/table/TableFilters";
import Loading from "../../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import { useNavigate } from "react-router-dom";
import { formatDateAndTime } from "../../../../../common/helpers/date.helpers";
import { ICommission } from "../../../../../types/commission.type";
import { RENTER_STATUS } from "../../../../../types/renter.type";
import { getCommission } from "../../../../../common/services/commission.api";
import { getEmployeeByType } from "../../../../../common/services/employee.api";
import { IEmployee, TYPE_EMPLOYEE } from "../../../../../types/employee.type";
import "./Commission.scss";

const STATUS_LABELS = {
  [TYPE_EMPLOYEE.OPERATOR]: "Consultor",
};
function getStatusLabels(status: RENTER_STATUS) {
  return STATUS_LABELS[status] || status;
}

const date = new Date();
date.setMonth(date.getMonth() - 2);
const headersCommission = {
  name: "Nome",
  type: "Cargo",
  activation: "Ativações",
  renew: "Renovações",
  activationPlus: "Ativação Plus",
  comissao: "Total Geral",
};

const subtitlesCommission = {
  name: "",
  type: "",
  activation: "",
  renew: "",
  activationPlus: "",
  comissao: "",
};

function TableCommission() {
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [commission, setCommission] = useState<ICommission[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [consultant, setConsultant] = useState<any[]>([]);
  const [data] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
  });

  const navigate = useNavigate();
  useEffect(() => {
    async function loadCommissionData() {
      setIsLoading(true);
      const [consultantRes, commissionRes] = await Promise.all([
        getEmployeeByType(),
        getCommission(),
      ]);

      if (!consultantRes.ok || !commissionRes.ok) {
        notifyError("Erro ao carregar dados de comissões.");
        setIsLoading(false);
        return;
      }

      const commissionData = commissionRes.data;
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

      const filteredCommissions = commissionData.filter((item) => {
        if (!item.activationDate) return false;
        const isOldEnough = new Date(item.activationDate) <= fourteenDaysAgo;
        const isNotPlus = item.typeCommission !== "activation-plus";
        return isOldEnough && isNotPlus;
      });

      const commissionsByEmployee = filteredCommissions.reduce((acc, cur) => {
        const employeeName = cur.technicalConsultant || cur.sdr;
        if (!employeeName) return acc;

        if (!acc[employeeName]) {
          acc[employeeName] = {
            activation: 0,
            renew: 0,
            "activation-plus": 0,
          };
        }

        if (cur.typeCommission === "activation") acc[employeeName].activation++;
        if (cur.typeCommission === "renew") acc[employeeName].renew++;
        if (cur.typeCommission === "activation-plus")
          acc[employeeName]["activation-plus"]++;

        return acc;
      }, {} as Record<string, { activation: number; renew: number; "activation-plus": number }>);

      const tableData = Array.isArray(consultantRes.data)
        ? consultantRes.data.map((employee) => {
            const empName = employee.name;
            const commissions = commissionsByEmployee[empName] || {
              activation: 0,
              renew: 0,
              "activation-plus": 0,
            };

            const totalActivation = Math.floor(commissions.activation / 3) * 6;
            const totalRenew = Math.floor(commissions.renew / 3) * 3;
            const totalPlus = commissions["activation-plus"] * 9;
            const totalCommission = totalActivation + totalRenew + totalPlus;

            return {
              name: empName,
              type: employee.type || employee.position,
              activation: commissions.activation,
              renew: commissions.renew,
              activationPlus: commissions["activation-plus"],
              comissao: `R$ ${totalCommission.toFixed(2).replace(".", ",")}`,
            };
          })
        : [];

      const sortedTableData = [...tableData].sort((a, b) => {
        const aTotal = a.activation + a.renew + a.activationPlus;
        const bTotal = b.activation + b.renew + b.activationPlus;
        return bTotal - aTotal;
      });

      setConsultant(sortedTableData);
      setCommission(commissionData);
      setIsLoading(false);
    }

    loadCommissionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section-commission">
        <TableFilters
          dashboardHeader="Relatório de Comissões"
          placeholder="Placa"
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo Cliente"
          onNewItemClick={() => {}}
          onSearch={handleFilterChange}
          entity="inventory-workorder"
          tableData={commission}
        />

        <GenericTable
          headers={headersCommission}
          data={consultant}
          subtitles={subtitlesCommission}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              status: getStatusLabels,
              name: (name, comission) => (
                <a
                  href={`/relatorios/comissoes/${
                    (comission as IEmployee).name
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `/relatorios/comissoes/${(comission as IEmployee).name}`
                    );
                  }}
                  target="_blank"
                >
                  {name as string}
                </a>
              ),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="commission"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TableCommission;
